import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { leaveService } from "../../api/leaveService";
import Modal from "../../components/ui/Modal";
import { useToast } from "../../components/ui/useToast";
import { getErrorMessage } from "../../utils/apiError";
import { countWorkingDays, formatDate } from "../../utils/date";
import { cx } from "../../utils/classNames";
import {
  availableForForm,
  calendarDaysBetween,
  findOverlappingLeave,
  LEAVE_STATUS,
  MY_LEAVE_BALANCES_KEY,
  MY_LEAVE_REQUESTS_KEY,
} from "./leaveUtils";
import ui from "../../components/ui/ui.module.css";

const MAX_REASON = 500;

const EMPTY_FORM = {
  leaveTypeId: "",
  startDate: "",
  endDate: "",
  isHalfDay: false,
  totalDays: "",
  reason: "",
};

// The form filled from the request being edited.
const formFromRequest = (request) => {
  const isHalfDay =
    request.startDate === request.endDate && Number(request.totalDays) === 0.5;
  return {
    leaveTypeId: request.leaveTypeId,
    startDate: request.startDate,
    endDate: request.endDate,
    isHalfDay,
    totalDays: String(Number(request.totalDays)),
    reason: request.reason ?? "",
  };
};

// Returns an error message, or "" when the form can be sent.
const validate = ({ form, year, balance, existingRequests, request }) => {
  const totalDays = Number(form.totalDays);
  const yearText = String(year);

  if (!form.leaveTypeId) return "Choose a leave type.";
  if (!form.startDate || !form.endDate)
    return "Choose the start and end dates.";
  if (form.endDate < form.startDate)
    return "End date must be on or after the start date.";
  if (
    !form.startDate.startsWith(yearText) ||
    !form.endDate.startsWith(yearText)
  ) {
    return `Both dates must be in ${year}. File one request per year.`;
  }

  const calendarDays = calendarDaysBetween(form.startDate, form.endDate);
  if (!totalDays || totalDays < 0.5) return "Total days must be at least 0.5.";
  if (!Number.isInteger(totalDays * 2))
    return "Total days must be in steps of 0.5.";
  if (totalDays > calendarDays) {
    return `Total days (${totalDays}) is more than the ${calendarDays} calendar day(s) selected.`;
  }
  const available = balance ? availableForForm(balance, request) : null;
  if (balance && totalDays > available) {
    return `Insufficient balance: ${available} day(s) available, ${totalDays} requested.`;
  }

  const overlap = findOverlappingLeave(
    existingRequests,
    form.startDate,
    form.endDate,
    request?.id,
  );
  if (overlap) {
    return `You already have a ${LEAVE_STATUS[overlap.status].label.toLowerCase()} leave from ${formatDate(
      overlap.startDate,
    )} to ${formatDate(overlap.endDate)}.`;
  }

  if (!form.reason.trim()) return "Please give a reason.";
  return "";
};

// File a new leave, or edit `request` (pending: approval restarts; approved:
// the new details wait for approval, the approved leave stays until accepted).
const FileLeaveModal = ({
  year,
  balances,
  existingRequests,
  request,
  onClose,
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const isEdit = !!request;
  const isApproved = request?.status === "approved";

  const [form, setForm] = useState(() =>
    request ? formFromRequest(request) : EMPTY_FORM,
  );
  const [isDaysEdited, setIsDaysEdited] = useState(false);
  const [error, setError] = useState("");

  const balance = balances.find((b) => b.leaveTypeId === form.leaveTypeId);
  const isSingleDay = !!form.startDate && form.startDate === form.endDate;
  const maxDays =
    calendarDaysBetween(form.startDate, form.endDate) || undefined;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: [MY_LEAVE_BALANCES_KEY] });
    queryClient.invalidateQueries({ queryKey: [MY_LEAVE_REQUESTS_KEY] });
  };

  const mutation = useMutation({
    mutationFn: (payload) =>
      isEdit
        ? leaveService.editMyLeave(request.id, payload)
        : leaveService.fileLeave(payload),
    onSuccess: () => {
      toast.success(
        !isEdit
          ? "Leave request submitted. Waiting for approval."
          : isApproved
            ? "Edit sent to your approver. Your approved leave stays until they accept."
            : "Leave updated. Waiting for approval again.",
      );
      refresh();
      onClose();
    },
    onError: (err) => {
      if (err?.response?.status === 409) refresh();
      setError(
        getErrorMessage(
          err,
          isEdit
            ? "Unable to save your changes."
            : "Unable to submit your leave request.",
        ),
      );
    },
  });

  // Dates drive the half-day toggle and the auto-calculated weekday count.
  const updateDates = (changes) => {
    setForm((prev) => {
      const next = { ...prev, ...changes };
      if (next.startDate && (!next.endDate || next.endDate < next.startDate)) {
        next.endDate = next.startDate;
      }
      if (next.startDate !== next.endDate) next.isHalfDay = false;

      if (next.isHalfDay) {
        next.totalDays = "0.5";
      } else if (!isDaysEdited) {
        const weekdays = countWorkingDays(next.startDate, next.endDate);
        next.totalDays = weekdays ? String(weekdays) : "";
      }
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const message = validate({
      form,
      year,
      balance,
      existingRequests,
      request,
    });
    setError(message);
    if (message) return;

    mutation.mutate({
      leaveTypeId: form.leaveTypeId,
      startDate: form.startDate,
      endDate: form.endDate,
      totalDays: Number(form.totalDays),
      reason: form.reason.trim(),
    });
  };

  return (
    <Modal
      as="form"
      onSubmit={handleSubmit}
      title={`${isEdit ? "Edit Leave" : "File Leave"} · ${year}`}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className={cx(ui.button, ui.secondary)}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={cx(ui.button, ui.primary)}
            disabled={mutation.isPending}
          >
            {mutation.isPending
              ? "Saving…"
              : !isEdit
                ? "Submit Request"
                : isApproved
                  ? "Send for approval"
                  : "Save changes"}
          </button>
        </>
      }
    >
      <div className={ui.formStack}>
        {isEdit && (
          <div className={cx(ui.alert, ui.alertInfo)}>
            {isApproved
              ? "This goes to your approver. Your approved leave stays as it is until they accept."
              : "Saving restarts the approval."}
          </div>
        )}
        <div>
          <label htmlFor="leave-type" className={ui.label}>
            Leave type *
          </label>
          <select
            id="leave-type"
            className={ui.input}
            value={form.leaveTypeId}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, leaveTypeId: e.target.value }))
            }
          >
            <option value="">
              {balances.length
                ? "Select leave type"
                : `No leave balances for ${year}`}
            </option>
            {isEdit &&
              !balances.some((b) => b.leaveTypeId === request.leaveTypeId) && (
                <option value={request.leaveTypeId}>
                  {request.leaveType?.name ?? "Leave"}
                </option>
              )}
            {balances.map((b) => {
              const available = availableForForm(b, request);
              return (
                <option
                  key={b.leaveTypeId}
                  value={b.leaveTypeId}
                  disabled={available <= 0}
                >
                  {b.leaveType?.name ?? "Leave"} — {available} day(s) available
                </option>
              );
            })}
          </select>
        </div>

        <div className={ui.formRow}>
          <div>
            <label htmlFor="leave-start" className={ui.label}>
              Start date *
            </label>
            <input
              id="leave-start"
              type="date"
              className={ui.input}
              min={`${year}-01-01`}
              max={`${year}-12-31`}
              value={form.startDate}
              onChange={(e) => updateDates({ startDate: e.target.value })}
            />
          </div>
          <div>
            <label htmlFor="leave-end" className={ui.label}>
              End date *
            </label>
            <input
              id="leave-end"
              type="date"
              className={ui.input}
              min={form.startDate || `${year}-01-01`}
              max={`${year}-12-31`}
              value={form.endDate}
              onChange={(e) => updateDates({ endDate: e.target.value })}
            />
          </div>
        </div>

        <div className={ui.formRow}>
          <div>
            <label htmlFor="leave-days" className={ui.label}>
              Total days *
            </label>
            <input
              id="leave-days"
              type="number"
              min="0.5"
              step="0.5"
              max={maxDays}
              className={ui.input}
              value={form.totalDays}
              disabled={form.isHalfDay}
              onChange={(e) => {
                setIsDaysEdited(true);
                setForm((prev) => ({ ...prev, totalDays: e.target.value }));
              }}
            />
            <small className={ui.hint}>
              Weekdays in the range. Adjust for holidays or weekend work.
            </small>
          </div>
          <div>
            <span className={ui.label}>Half day</span>
            <label
              className={ui.checkbox}
              title={isSingleDay ? undefined : "Only for single-day leave"}
            >
              <input
                type="checkbox"
                checked={form.isHalfDay}
                disabled={!isSingleDay}
                onChange={(e) => updateDates({ isHalfDay: e.target.checked })}
              />
              Half day (0.5)
            </label>
          </div>
        </div>

        <div>
          <label htmlFor="leave-reason" className={ui.label}>
            Reason *
          </label>
          <textarea
            id="leave-reason"
            rows={3}
            maxLength={MAX_REASON}
            className={cx(ui.input, ui.textarea)}
            value={form.reason}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, reason: e.target.value }))
            }
            placeholder="e.g. Family matters"
          />
          <small className={ui.hint}>
            {form.reason.length}/{MAX_REASON}
          </small>
        </div>

        {error && <div className={cx(ui.alert, ui.alertDanger)}>{error}</div>}
      </div>
    </Modal>
  );
};

export default FileLeaveModal;
